import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Warning,
  Assessment,
  Error,
  CheckCircle,
} from '@mui/icons-material';
import { fraudUtils } from '../services/api';

interface FraudAnalysisSummaryProps {
  summary: any;
  irregularityType: string;
}

const FraudAnalysisSummary: React.FC<FraudAnalysisSummaryProps> = ({ 
  summary, 
  irregularityType 
}) => {
  const getIrregularityTypeInfo = (type: string) => {
    const typeMap = {
      'competition-analysis': { 
        name: 'Competition Analysis', 
        icon: '🔍',
        description: 'Comprehensive competition fraud detection'
      },
      'sole-source': { 
        name: 'Sole Source Investigation', 
        icon: '🚫',
        description: 'Contracts awarded without competition'
      },
      'low-competition': { 
        name: 'Low Competition Analysis', 
        icon: '⚠️',
        description: 'Limited bidder patterns detected'
      },
      'vendor-concentration': { 
        name: 'Vendor Concentration', 
        icon: '🎯',
        description: 'Multiple awards to single vendors'
      },
      'split-purchase': { 
        name: 'Split Purchase Detection', 
        icon: '✂️',
        description: 'Orders split to avoid thresholds'
      },
      'excessive-pricing': { 
        name: 'Excessive Pricing Analysis', 
        icon: '💰',
        description: 'Above-market pricing detected'
      },
      'timing-anomaly': { 
        name: 'Timing Anomaly Detection', 
        icon: '⏰',
        description: 'Suspicious timing patterns'
      },
      'emergency-misuse': { 
        name: 'Emergency Misuse Analysis', 
        icon: '🚨',
        description: 'Emergency authority abuse'
      },
    };
    
    return typeMap[type as keyof typeof typeMap] || { 
      name: 'Irregularity Analysis', 
      icon: '🔍',
      description: 'Pattern analysis completed'
    };
  };

  const typeInfo = getIrregularityTypeInfo(irregularityType);

  // Extract key metrics from summary - handling different structures for different irregularity types
  let totalContracts, suspiciousContracts, suspiciousPercentage, criticalCount, highRiskCount, mediumRiskCount, lowRiskCount, totalValueSuspicious, investigationPriority, patternsDetected;
  
  // Check if this is a low-competition or similar type with different structure
  if (summary?.total_analyzed !== undefined && summary?.suspicious_found !== undefined) {
    // Low-competition and similar types structure
    totalContracts = summary.total_analyzed || 0;
    suspiciousContracts = summary.suspicious_found || 0;
    suspiciousPercentage = totalContracts > 0 ? (suspiciousContracts / totalContracts) * 100 : 0;
    
    // For these types, high_risk_count and medium_risk_count are at top level
    criticalCount = 0; // Not typically in this structure
    highRiskCount = summary.high_risk_count || 0;
    mediumRiskCount = summary.medium_risk_count || 0;
    lowRiskCount = summary.low_risk_count || 0;
    
    // Parse value at risk - handle string format like "$0.00"
    const valueStr = summary.total_value_at_risk || summary.total_value || 0;
    totalValueSuspicious = typeof valueStr === 'string' ? 
      parseFloat(valueStr.replace(/[$,]/g, '')) || 0 : valueStr;
    
    // Determine priority based on risk counts
    if (highRiskCount >= 5) {
      investigationPriority = 'CRITICAL';
    } else if (highRiskCount >= 2) {
      investigationPriority = 'HIGH';
    } else if (mediumRiskCount >= 3) {
      investigationPriority = 'MEDIUM';
    } else {
      investigationPriority = 'LOW';
    }
    
    // Use patterns_detected array directly
    patternsDetected = summary.patterns_detected || [];
  } else {
    // Sole-source and similar types structure
    totalContracts = summary?.total_contracts || 0;
    suspiciousContracts = summary?.suspicious_contracts || 0;
    suspiciousPercentage = summary?.suspicious_percentage || 0;
    
    const byRiskLevel = summary?.by_risk_level || {};
    criticalCount = byRiskLevel?.CRITICAL || 0;
    highRiskCount = byRiskLevel?.HIGH || 0;
    mediumRiskCount = byRiskLevel?.MEDIUM || 0;
    lowRiskCount = byRiskLevel?.LOW || 0;
    
    totalValueSuspicious = summary?.total_value_suspicious || 0;
    investigationPriority = summary?.investigation_priority || 'MEDIUM';
    
    // Convert common risk patterns to array
    const commonRiskPatterns = summary?.common_risk_patterns || {};
    patternsDetected = Object.entries(commonRiskPatterns).map(
      ([pattern, count]) => `${pattern} (${count} occurrences)`
    );
  }
  
  // Get recommendation - available in both structures
  const recommendation = summary?.recommendation || null;
  const topRisks = summary?.top_risks || [];

  return (
    <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>{typeInfo.icon}</span>
          {typeInfo.name} - Investigation Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {typeInfo.description}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Key Metrics */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ pb: 1 }}>
                  <Typography variant="h4" color="primary">
                    {totalContracts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Analyzed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ pb: 1 }}>
                  <Typography variant="h4" color={criticalCount > 0 ? 'error' : highRiskCount > 0 ? 'warning' : 'success'}>
                    {criticalCount + highRiskCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    High/Critical Risk
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ pb: 1 }}>
                  <Typography variant="h4" color={mediumRiskCount > 0 ? 'warning' : 'success'}>
                    {mediumRiskCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Medium Risk
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ pb: 1 }}>
                  <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                    {fraudUtils.formatCurrency(totalValueSuspicious)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Value at Risk
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Risk Assessment */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assessment /> Risk Assessment
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Investigation Priority: <strong>{investigationPriority}</strong>
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, suspiciousPercentage)}
                  color={suspiciousPercentage > 75 ? 'error' : suspiciousPercentage > 50 ? 'warning' : 'success'}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {suspiciousPercentage.toFixed(1)}% suspicious ({suspiciousContracts}/{totalContracts})
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {criticalCount > 0 && (
                  <Chip
                    label={`${criticalCount} Critical`}
                    color="error"
                    size="small"
                    icon={<Error fontSize="small" />}
                  />
                )}
                {highRiskCount > 0 && (
                  <Chip
                    label={`${highRiskCount} High`}
                    color="error"
                    size="small"
                    variant="outlined"
                  />
                )}
                {mediumRiskCount > 0 && (
                  <Chip
                    label={`${mediumRiskCount} Medium`}
                    color="warning"
                    size="small"
                    icon={<Warning fontSize="small" />}
                  />
                )}
                {lowRiskCount > 0 && (
                  <Chip
                    label={`${lowRiskCount} Low`}
                    color="info"
                    size="small"
                  />
                )}
                {criticalCount === 0 && highRiskCount === 0 && mediumRiskCount === 0 && (
                  <Chip
                    label="No Critical Issues"
                    color="success"
                    size="small"
                    icon={<CheckCircle fontSize="small" />}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Risk Indicators - only for low-competition and similar types */}
      {topRisks.length > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
            🚨 Key Risk Indicators:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {topRisks.slice(0, 5).map((risk: string, index: number) => (
              <Chip
                key={index}
                label={risk}
                size="small"
                variant="outlined"
                color="error"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Recommendation based on investigation priority */}
      <Alert 
        severity={
          investigationPriority === 'CRITICAL' ? 'error' :
          investigationPriority === 'HIGH' ? 'warning' :
          investigationPriority === 'MEDIUM' ? 'info' : 'success'
        } 
        sx={{ mt: 3 }}
        icon={investigationPriority === 'HIGH' || investigationPriority === 'CRITICAL' ? <Warning /> : <CheckCircle />}
      >
        <Typography variant="body2">
          <strong>Investigation Recommendation:</strong> 
          {recommendation ? ` ${recommendation}` :
           investigationPriority === 'CRITICAL' ? ' Immediate investigation required. Multiple high-risk patterns detected.' :
           investigationPriority === 'HIGH' ? ' Priority investigation recommended. Significant fraud indicators present.' :
           investigationPriority === 'MEDIUM' ? ' Standard review recommended. Some irregularities detected.' :
           ' Routine monitoring sufficient. Low risk indicators.'}
        </Typography>
      </Alert>

      {/* Investigation Note */}
      {(criticalCount > 0 || highRiskCount > 0) && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            💡 <strong>Investigation Tip:</strong> Focus on the {criticalCount > 0 ? criticalCount + ' critical' : highRiskCount + ' high'}-risk contracts first. 
            {patternsDetected.length > 0 && `Common patterns include: ${patternsDetected[0]}.`}
            Consider cross-referencing with other databases for additional context.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default FraudAnalysisSummary;